import { Card, Col, Divider, Form, Row, Space, Select } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ButtonAction from 'components/Common/ButtonAction';
import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks';
import CustomAntdTable from 'components/Common/CustomAntdTable';
import { dismissToast, loadingToastId } from 'utils/Toast';
import Constant from 'utils/Constants';
import { sorterTable } from 'utils/Helper';
import useIsMobileHooks from 'hooks/useIsMobileHooks';
import SkeletonPage from 'components/Common/SkeletonPage';
import { ButtonAdvanceFilter } from 'components/Common/ButtonAdvanceFilter';
import Error403 from 'components/Error/Error403';
import BaseAdvanceFilter from 'components/CustomDrawer/BaseAdvanceFilter';
import CellTable from 'components/Common/CellTable';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import useOrgRepo from 'services/repo/useOrgRepo';
import useDeptRepo from 'services/repo/useDepartmentRepo';
import useDivRepo from 'services/repo/useDivisionRepo';
import usePositionRepo from 'services/repo/usePositionRepo';
import useReportRepo from 'services/repo/useReportRepo';

// util shallow equality
const shallowEqual = (a = {}, b = {}) => {
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
};

export default function WbsReport() {
  useTopbarTitleHooks('Report Work Breakdown Structure');

  const { isLoading: isLoadingDeptList, getDepartmentList } = useDeptRepo(false);
  const { isLoading: isLoadingOrgList, getOrgList } = useOrgRepo(false);
  const { isLoading: isLoadingDivList, getDivisionList } = useDivRepo(false);
  const { isLoading: isLoadingPosList, getPositionList } = usePositionRepo(false);
  const { isLoading: isLoadingReport, getReportWbsList } = useReportRepo(false);

  const isMobile = useIsMobileHooks();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // privilege simplified
  const [privLoaded, setPrivLoaded] = useState(false);
  const [hasPrivilege, setHasPrivilege] = useState(true);

  // table data
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);

  // single source of truth untuk filter
  const [filters, setFilters] = useState({
    orgId: null,
    divId: null,
    deptId: null,
    posId: null,
    wfId: null,
  });

  // pagination + sorting
  const [paginationSort, setPaginationSort] = useState({
    pageNumber: 1,
    pageSize: 10,
    orderBy: 'wbsNo',
    isAscending: true,
  });

  const [isOpenAdvanceFilter, setIsOpenAdvanceFilter] = useState(false);

  // options dropdown
  const [orgOptions, setOrgOptions] = useState([]);
  const [divOptions, setDivOptions] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [posOptions, setPosOptions] = useState([]);

  // --- payload & tableParam (memoized identity) ---
  const payload = useMemo(() => ({
    pageNumber: paginationSort.pageNumber,
    pageSize: paginationSort.pageSize,
    orderBy: paginationSort.orderBy,
    isAscending: paginationSort.isAscending,
    filter: { ...filters },
  }), [paginationSort.pageNumber, paginationSort.pageSize, paginationSort.orderBy, paginationSort.isAscending, filters]);

  const tableParamMemo = useMemo(() => ({
    ...paginationSort,
    filter: filters,
  }), [paginationSort, filters]);

  // guard fetch duplikat untuk table data
  const lastPayloadRef = useRef(null);
  const isSamePayload = (a, b) => {
    if (!a || !b) return false;
    return (
      a.pageNumber === b.pageNumber &&
      a.pageSize === b.pageSize &&
      a.orderBy === b.orderBy &&
      a.isAscending === b.isAscending &&
      shallowEqual(a.filter, b.filter)
    );
  };

  const handleView = useCallback((row) => {
    const key = 'loadingView';
    loadingToastId(Constant.message.loading, key);
    const targetPath = `${APP_ROUTE_PREFIX}/${RouteConstant.report.reportWbsDetail(row?.wbsId)}`;
    navigate(targetPath);
    dismissToast(key);
  }, [navigate]);

  // skip onChange pertama dari Table (beberapa wrapper memanggil onChange saat mount)
  const skippedFirstChangeRef = useRef(false);

  const handleTableChange = useCallback((pagination, _filters, sorter) => {
    if (!skippedFirstChangeRef.current) {
      skippedFirstChangeRef.current = true; // skip sekali saat mount
      return;
    }

    const incomingSize = pagination?.pageSize ?? paginationSort.pageSize;
    const sizeChanged = incomingSize !== paginationSort.pageSize;

    setPaginationSort(prev => {
      const next = {
        ...prev,
        pageNumber: pagination?.current ?? prev.pageNumber,
        pageSize: incomingSize,
        orderBy: sorter?.field || prev.orderBy,
        isAscending: sorterTable(sorter?.order, prev.isAscending),
      };
      const same =
        next.pageNumber === prev.pageNumber &&
        next.pageSize === prev.pageSize &&
        next.orderBy === prev.orderBy &&
        next.isAscending === prev.isAscending;
      return same ? prev : next;
    });

    if (sizeChanged) setData([]);
  }, [paginationSort.pageSize]);

  const onSearch = useCallback(() => {
    setPaginationSort(prev => {
      const next = { ...prev, pageNumber: 1 };
      return next.pageNumber === prev.pageNumber ? prev : next;
    });
  }, []);

  const onSearchClick = useCallback(() => {
    onSearch();
    setIsOpenAdvanceFilter(false);
  }, [onSearch]);

  // ---- fetchers ----
  const fetchData = useCallback(async (p) => {
    const res = await getReportWbsList(p);
    if (res?.detail === 'OK' && res?.status === 200) {
      setData(res?.data?.content ?? []);
      setTotalData(res?.data?.totalElements ?? 0);
    } else {
      setData([]);
      setTotalData(0);
    }
  }, [getReportWbsList]);

  const fetchDataDivision = useCallback(async (orgId) => {
    try {
      setDivOptions([]);
      const res = await getDivisionList({
        pageNumber: 1,
        pageSize: 0,
        filter: { divCode: '', divName: '', orgId, divId: null, status: 'true' },
        orderBy: 'divName',
        isAscending: true,
      });
      const list = res?.data?.content ?? [];
      setDivOptions(list.map(item => ({ value: item?.divId, label: item?.divName })));
    } catch {
      setDivOptions([]);
    }
  }, [getDivisionList]);

  const fetchDataDepartment = useCallback(async (divId) => {
    try {
      setDeptOptions([]);
      const res = await getDepartmentList({
        pageNumber: 1,
        pageSize: 0,
        filter: { deptId: null, deptName: '', divId, status: 'true' },
        orderBy: 'deptName',
        isAscending: true,
      });
      const list = res?.data?.content ?? [];
      setDeptOptions(list.map(item => ({ value: item?.deptId, label: item?.deptName })));
    } catch {
      setDeptOptions([]);
    }
  }, [getDepartmentList]);

  const fetchDataPosition = useCallback(async (deptId) => {
    try {
      setPosOptions([]);
      const res = await getPositionList({
        pageNumber: 1,
        pageSize: 0,
        filter: { posId: null, posName: '', deptId, status: 'true' },
        orderBy: 'posName',
        isAscending: true,
      });
      const list = res?.data?.content ?? [];
      setPosOptions(list.map(item => ({ value: item?.posId, label: item?.posName })));
    } catch {
      setPosOptions([]);
    }
  }, [getPositionList]);

  // ---- ONE-TIME INIT (fix reload berulang) ----
  const didInitRef = useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true; // guard agar hanya sekali (mengatasi StrictMode & identity function)

    (async () => {
      // privilege (mock ok)
      setHasPrivilege(true);
      setPrivLoaded(true);

      // fetch organisasi sekali
      try {
        const res = await getOrgList({
          pageNumber: 1,
          pageSize: 0,
          orderBy: 'orgId',
          isAscending: true,
          filter: { orgId: null, orgName: '', status: 'true' },
        });
        const list = res?.data?.content ?? [];
        const options = list.map(item => ({ label: item?.orgName, value: item?.orgId }));
        setOrgOptions(options);

        // auto-pilih jika hanya 1
        if (options.length === 1) {
          const only = options[0];
          form.setFieldsValue({ orgId: only.value }); // ID saja
          setFilters(prev => ({ ...prev, orgId: only.value, divId: null, deptId: null, posId: null }));

          // prefetch division untuk org tersebut
          try {
            const resDiv = await getDivisionList({
              pageNumber: 1,
              pageSize: 0,
              filter: { divCode: '', divName: '', orgId: only.value, divId: null, status: 'true' },
              orderBy: 'divName',
              isAscending: true,
            });
            const listDiv = resDiv?.data?.content ?? [];
            setDivOptions(listDiv.map(it => ({ value: it?.divId, label: it?.divName })));
          } catch {
            setDivOptions([]);
          }
        }
      } catch {
        setOrgOptions([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch data table HANYA saat payload berubah & privilege siap
  useEffect(() => {
    if (!privLoaded) return;
    if (isSamePayload(payload, lastPayloadRef.current)) return;
    lastPayloadRef.current = payload;
    fetchData(payload);
  }, [privLoaded, payload, fetchData]);

  // debounce kecil saat filters berubah (desktop only)
  useEffect(() => {
    if (!privLoaded || isMobile) return;
    const t = setTimeout(() => onSearch(), Constant.delayFilter);
    return () => clearTimeout(t);
  }, [filters, privLoaded, isMobile, onSearch]);

  // ---- UI helpers ----
  const columns = useMemo(() => ([
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <ButtonAction buttons={['view']} onView={() => handleView(record)} data={record} />
      ),
    },
    { title: 'WBS Form No', dataIndex: 'wbsNo', sorter: true, width: 220, render: (v) => <CellTable data={v} /> },
    { title: 'Status', dataIndex: 'wfValue', sorter: true, width: 120, render: (v) => <CellTable data={v} /> },
    { title: 'PIC', dataIndex: 'submissionUserFullName', sorter: true, width: 200, render: (v) => <CellTable data={v} /> },
    { title: 'Position', dataIndex: 'posName', sorter: true, width: 200, render: (v) => <CellTable data={v} /> },
    { title: 'Approver', dataIndex: 'approverUserFullName', sorter: true, width: 200, render: (v) => <CellTable data={v} /> },
    { title: 'Create Date', dataIndex: 'createdDate', sorter: true, width: 170, render: (v) => <CellTable data={v} /> },
    { title: 'Last Update', dataIndex: 'updatedDate', sorter: true, width: 170, render: (v) => <CellTable data={v} /> },
    { title: 'Update BY', dataIndex: 'updatedByFullName', sorter: true, width: 200, render: (v) => <CellTable data={v} /> },
  ]), [handleView]);

  const filterUI = (
    <Form layout="horizontal" form={form}>
      <Row gutter={16}>
        <Col xs={24} sm={10} md={8}>
          <Form.Item name="orgId" label="Organization" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
            <Select
              style={{ width: '100%' }}
              allowClear
              showSearch
              placeholder="Select Organization"
              loading={isLoadingOrgList}
              options={orgOptions}
              optionFilterProp="label"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              onChange={(value) => {
                const next = { ...filters, orgId: value || null, divId: null, deptId: null, posId: null };
                if (!shallowEqual(filters, next)) setFilters(next);
                form.setFieldsValue({ divId: undefined, deptId: undefined, posId: undefined });
                if (value != null) {
                  fetchDataDivision(value);
                } else {
                  setDivOptions([]); setDeptOptions([]); setPosOptions([]);
                }
              }}
            />
          </Form.Item>

          <Form.Item name="divId" label="Division" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
            <Select
              style={{ width: '100%' }}
              showSearch
              allowClear
              placeholder="Select Division"
              loading={isLoadingDivList}
              disabled={!filters?.orgId}
              options={divOptions}
              optionFilterProp="label"
              onChange={(value) => {
                const next = { ...filters, divId: value || null, deptId: null, posId: null };
                if (!shallowEqual(filters, next)) setFilters(next);
                form.setFieldsValue({ deptId: undefined, posId: undefined });
                if (value != null) {
                  fetchDataDepartment(value);
                } else {
                  setDeptOptions([]); setPosOptions([]);
                }
              }}
            />
          </Form.Item>

          <Form.Item name="deptId" label="Department" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
            <Select
              style={{ width: '100%' }}
              showSearch
              allowClear
              placeholder="Select Department"
              loading={isLoadingDeptList}
              disabled={!filters?.divId}
              options={deptOptions}
              optionFilterProp="label"
              onChange={(value) => {
                const next = { ...filters, deptId: value || null, posId: null };
                if (!shallowEqual(filters, next)) setFilters(next);
                form.setFieldsValue({ posId: undefined });
                if (value != null) fetchDataPosition(value);
                else setPosOptions([]);
              }}
            />
          </Form.Item>

          <Form.Item name="posId" label="Posisi" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }}>
            <Select
              style={{ width: '100%' }}
              showSearch
              allowClear
              placeholder="Select Position"
              loading={isLoadingPosList}
              disabled={!filters?.deptId}
              options={posOptions}
              optionFilterProp="label"
              onChange={(value) => {
                const next = { ...filters, posId: value || null };
                if (!shallowEqual(filters, next)) setFilters(next);
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12} />
      </Row>
    </Form>
  );

  if (!hasPrivilege) return <Error403 />;

  return (
    <Space direction="vertical" size="large" className="space-vertical-flex">
      <Card size="big" bordered={false}>
        {!privLoaded ? (
          <SkeletonPage />
        ) : (
          <>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={20} xl={18} />
            </Row>

            <Divider dashed className="mb-1 mt-1" />

            {isMobile ? (
              <ButtonAdvanceFilter onClick={() => setIsOpenAdvanceFilter(true)} />
            ) : (
              filterUI
            )}

            <CustomAntdTable
              columns={columns}
              dataSource={data}
              rowKey="wbsId"
              tableParam={tableParamMemo}
              totalData={totalData}
              loading={isLoadingReport}
              onChange={handleTableChange}
            />

            {isOpenAdvanceFilter && (
              <BaseAdvanceFilter onClose={() => setIsOpenAdvanceFilter(false)} onSearchClick={onSearchClick}>
                {filterUI}
              </BaseAdvanceFilter>
            )}
          </>
        )}
      </Card>
    </Space>
  );
}





